<!--
 - Orders List component.
 -
 - @author Ryan Sweeney <rsweeney@buildcentrix.com>
 - @copyright (c) 2024 Build Centrix
 -->
<form #ordersListForm="ngForm" class="bcx-orders-v2">
  <!--Header-->
  <div class="order-header row mb-1">
    <div class="w-100" *ngIf="!wdOrdersService.isLoadingOrders.value">
      <app-orders-list-v2-header-component
        [orderType]="orderType"
        [pageTitle]="pageTitle"
        [pageSubtitle]="pageSubtitle"
        (tabChanges)="changeDetector.detectChanges()"
      ></app-orders-list-v2-header-component>
    </div>
  </div>

  <!--Content-->
  <div
    class="order-content row"
    [ngClass]="{ 'loaded': wdOrdersService.isReloadingOrders.value === false, 'loading': wdOrdersService.isReloadingOrders.value === true}"
  >
    <div class="col-sm-12">
      <!--Page Loader-->
      <div
        class="bcx-loader-container"
        *ngIf="(wdOrdersService.isLoadingOrders|async) === true"
      >
        <bc-atoms-loader
          [title]="'actions.loadingType'|translate: {type:('order.title.plural'|translate)}"
          [active]="true"
          [fullscreen]="false"
        ></bc-atoms-loader>
      </div>

      <!--Order Content-->
      <div
        class="bcx-order-container"
        *ngIf="(wdOrdersService.isLoadingOrders|async) === false"
      >
        <div class="order-list-content row">
          <!--Order Tabs-->
          <div class="col-sm-12 text-center tabs-top">
            <app-orders-list-tabs-component
              [orderType]="orderType"
            ></app-orders-list-tabs-component>
          </div>
          <!--List Orders  -->
          <div class="col-sm-12">
            <!--Orders Table-->
            <bc-templates-table-container
              class="bcx-orders-table"
              [dataSource]="orderListDataSource"
              [headers]="wdOrdersService.orderListColumns"
              [isLoading]="(wdOrdersService.isLoadingOrders|async)"
              [paginationPageSize]="wdOrdersService.orderViewSettings.pageSize"
              [customColSpan]="columnHeaders.length"
              matSort
            >
              <!-- Checkbox Column -->
              <ng-container matColumnDef="select">
                <th
                  mat-header-cell
                  class="select-column static-column"
                  *matHeaderCellDef
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <!--Select All Checkbox-->
                  <mat-checkbox
                    (change)="$event ? toggleAllCheckboxes() : null"
                    disableRipple="true"
                    [checked]="wdOrdersService.allOrdersSelected.hasValue() && isAllSelected()"
                    [indeterminate]="wdOrdersService.allOrdersSelected.hasValue() && !isAllSelected()"
                    [title]="setCheckboxLabel()"
                    color="primary"
                  >
                  </mat-checkbox>
                </th>
                <td
                  mat-cell
                  *matCellDef="let element"
                  class="select-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <!--Select Row Checkbox-->
                  <mat-checkbox
                    (click)="$event.stopPropagation()"
                    (change)="$event ? wdOrdersService.allOrdersSelected.toggle(element) : null"
                    disableRipple="true"
                    [checked]="wdOrdersService.allOrdersSelected.isSelected(element)"
                    [title]="setCheckboxLabel(element, ('order.details.number.title'|translate) + element.number)"
                    color="primary"
                  >
                  </mat-checkbox>
                </td>
              </ng-container>

              <!--Order Number-->
              <!--Also includes special instructions & attachments/adjustments count-->
              <ng-container matColumnDef="number">
                <th
                  class="order-column static-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.details.number.description'|translate}}"
                >
                  {{'order.details.number.title'|translate}}
                </th>
                <td
                  class="order-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.details.number.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true,
                  'no-select': !this.wdEnvironmentService.permissions.canChangeOrderStatus }"
                >
                  <span
                    [ngbPopover]="adjustmentsPopover"
                    [autoClose]="'outside'"
                    placement="bottom"
                    triggers="manual"
                    #adjustmentsCountPopover="ngbPopover"
                    (mouseenter)="toggleAdjustmentsPopover(adjustmentsCountPopover, element.meta?.adjustments?.count)"
                    (mouseleave)="toggleAdjustmentsPopover(adjustmentsCountPopover, element.meta?.adjustments?.count)"
                  >
                    <!--Order Number-->
                    <a
                      [routerLink]="['/orders/manage/', element.index]"
                      target="_blank"
                    >
                      {{element.number}}
                    </a>
                    <span
                      class="adjustments-indicator"
                      *ngIf="element.meta?.adjustments?.count > 0"
                    >
                      <bc-atoms-icon type="adjustment"></bc-atoms-icon>
                    </span>
                  </span>
                </td>
              </ng-container>

              <!--Date Ordered-->
              <ng-container matColumnDef="dateOrdered">
                <th
                  class="date-column ordered static-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.date.ordered.description'|translate}}"
                >
                  {{'order.columns.date.ordered.title'|translate}}
                </th>
                <td
                  class="date-column ordered bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.date.ordered.title'|translate"
                  [title]="element.ordered?.date|date:wdEnvironmentService.datePipeFormat"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    >{{element.ordered?.date|date:wdEnvironmentService.datePipeFormat}}</span
                  >
                </td>
              </ng-container>

              <!--************************************** Details Columns ******************************************* -->

              <!--Date Requested-->
              <ng-container matColumnDef="dateRequested">
                <th
                  class="date-column requested"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.date.requested.description'|translate}}"
                >
                  {{'order.columns.date.requested.title'|translate}}
                </th>
                <td
                  class="date-column requested bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.date.requested.title'|translate"
                  [title]="element.requested?.date|date:wdEnvironmentService.datePipeFormat"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    >{{element.requested?.date|date:wdEnvironmentService.datePipeFormat}}</span
                  >
                </td>
              </ng-container>

              <!--Lookahead-->
              <ng-container matColumnDef="lookahead">
                <th
                  class=""
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.lookahead.description'|translate}}"
                >
                  {{'order.columns.lookahead.title'|translate}}
                </th>
                <td
                  class="bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.lookahead.title'|translate"
                  [title]="element.tag"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span>{{element.tag}}</span>
                </td>
              </ng-container>

              <!--Job Number-->
              <ng-container matColumnDef="jobNumber">
                <th
                  class="job-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.job.number.description'|translate}}"
                >
                  {{'order.columns.job.number.title'|translate}}
                </th>
                <td
                  class="job-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.job.number.title'|translate"
                  [title]="element.job?.number"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span *ngIf="element.settings.canEditEcomJobOverride">
                    <a
                      target="_blank"
                      [ngbPopover]="popContentOverride"
                      [autoClose]="'outside'"
                      placement="bottom"
                      triggers="manual"
                      #orderOverridePopover="ngbPopover"
                      (click)="toggleOrderOverridePopover(orderOverridePopover, element, 'number', 'Number')"
                    >
                      <bc-atoms-button
                        [label]="element.job?.override?.number"
                        [title]="element.job?.override?.number"
                        type="link"
                        icon="edit"
                        [loading]="element.job?.override['isUpdatingNumber']"
                      >
                      </bc-atoms-button>
                    </a>
                  </span>
                  <span *ngIf="!element.settings.canEditEcomJobOverride">
                    <span *ngIf="element.job?.number">
                      {{element.job?.number}}
                    </span>
                    <span *ngIf="!element.job?.number" class="text-danger"
                      >{{'errors.unknown'|translate}}</span
                    >
                  </span>
                </td>
              </ng-container>

              <!--Job Name-->
              <ng-container matColumnDef="jobName">
                <th
                  class="job-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.job.name.description'|translate}}"
                >
                  {{'order.columns.job.name.title'|translate}}
                </th>
                <td
                  class="job-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.job.name.title'|translate"
                  [title]="element.job?.name"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span *ngIf="element.settings.canEditEcomJobOverride">
                    <a
                      target="_blank"
                      [ngbPopover]="popContentOverride"
                      [autoClose]="'outside'"
                      placement="bottom"
                      triggers="manual"
                      #orderOverridePopover="ngbPopover"
                      (click)="toggleOrderOverridePopover(orderOverridePopover, element, 'name', 'Name')"
                    >
                      <bc-atoms-button
                        [label]="element.job?.override?.name"
                        [title]="element.job?.override?.name"
                        type="link"
                        icon="edit"
                        icon="edit"
                        [loading]="element.job?.override['isUpdatingName']"
                      >
                      </bc-atoms-button>
                    </a>
                  </span>
                  <span *ngIf="!element.settings.canEditEcomJobOverride">
                    <span *ngIf="element.job?.name">
                      {{element.job?.name}}
                    </span>
                    <span *ngIf="!element.job?.name" class="text-danger">
                      {{'errors.missingTypeData'|translate: {
                      type:('order.title.singular'|translate) } }}
                    </span>
                  </span>
                </td>
              </ng-container>

              <!--Job Phase-->
              <ng-container matColumnDef="jobPhase">
                <th
                  class="job-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.job.phase.description'|translate}}"
                >
                  {{'order.columns.job.phase.title'|translate}}
                </th>
                <td
                  class="job-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.job.phase.title'|translate"
                  [title]="element.job?.phase?.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span *ngIf="element.job?.phase"
                    >{{element.job?.phase?.label}}</span
                  >
                  <span *ngIf="!element.job?.phase">
                    <span
                      class="text-uppercase"
                      *ngIf="element.type === 'ecommerce'"
                    >
                      {{'order.types.ecommerce.description'|translate}}
                    </span>
                  </span>
                </td>
              </ng-container>

              <!--Ordered By-->
              <ng-container matColumnDef="orderedBy">
                <th
                  class="user-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.ordered.by.description'|translate}}"
                >
                  {{'order.columns.ordered.by.title'|translate}}
                </th>
                <td
                  class="user-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.ordered.by.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    [ngbPopover]="userDetailsPopover"
                    [autoClose]="'outside'"
                    placement="bottom"
                    triggers="mouseenter:mouseleave:click"
                    #orderedByPopover="ngbPopover"
                    (click)="toggleUserDetailsPopover(orderedByPopover, element.orderedBy)"
                    (mouseenter)="toggleUserDetailsPopover(orderedByPopover, element.orderedBy)"
                    (mouseleave)="toggleUserDetailsPopover(orderedByPopover, element.orderedBy)"
                  >
                    {{element.orderedBy?.name?.last}},
                    {{element.orderedBy?.name?.first}}
                  </span>
                </td>
              </ng-container>

              <!--Ordered By-->
              <ng-container matColumnDef="orderedFor">
                <th
                  class="user-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.ordered.for.description'|translate}}"
                >
                  {{'order.columns.ordered.for.title'|translate}}
                </th>
                <td
                  class="user-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.ordered.for.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    [ngbPopover]="userDetailsPopover"
                    [autoClose]="'outside'"
                    placement="bottom"
                    triggers="mouseenter:mouseleave:click"
                    #orderedForPopover="ngbPopover"
                    (click)="toggleUserDetailsPopover(orderedForPopover, element.orderedFor)"
                    (mouseenter)="toggleUserDetailsPopover(orderedForPopover, element.orderedFor)"
                    (mouseleave)="toggleUserDetailsPopover(orderedForPopover, element.orderedFor)"
                  >
                    {{element.orderedFor?.name?.last}},
                    {{element.orderedFor?.name?.first}}
                  </span>
                </td>
              </ng-container>

              <!--PO Number-->
              <ng-container matColumnDef="poNumber">
                <th
                  class=""
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.poNumber.description'|translate}}"
                >
                  {{'order.columns.poNumber.title'|translate}}
                </th>
                <td
                  class="bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.poNumber.title'|translate"
                  [title]="element.poNumber"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span>{{element.poNumber}}</span>
                </td>
              </ng-container>

              <!--Tag-->
              <ng-container matColumnDef="tag">
                <th
                  class=""
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.tag.description'|translate}}"
                >
                  {{'order.columns.tag.title'|translate}}
                </th>
                <td
                  class="bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.tag.title'|translate"
                  [title]="element.tag"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span>{{element.tag}}</span>
                </td>
              </ng-container>

              <!--User Group-->
              <ng-container matColumnDef="userGroup">
                <th
                  class=""
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.user.group.description'|translate}}"
                >
                  {{'order.columns.user.group.title'|translate}}
                </th>
                <td
                  class="bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.user.group.title'|translate"
                  [title]="element.userGroup?.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span>{{element.userGroup?.label}}</span>
                </td>
              </ng-container>

              <!--Special Instructions-->
              <ng-container matColumnDef="specialInstructions">
                <th
                  class="instructions-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.specialInstructions.description'|translate}}"
                >
                  {{'order.columns.specialInstructions.title'|translate}}
                </th>
                <td
                  class="instructions-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.specialInstructions.title'|translate"
                  [title]="element.specialInstructions"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span>{{element.specialInstructions}}</span>
                </td>
              </ng-container>

              <!--************************************** Production Columns ******************************************* -->

              <!--Production Date Start-->
              <ng-container matColumnDef="dateProductionStart">
                <th
                  class="date-column production"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.date.production.start.description'|translate}}"
                >
                  {{'order.columns.date.production.start.title'|translate}}
                </th>
                <td
                  class="date-column production bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.date.production.start.title'|translate"
                  [title]="element.production.start?.date"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-date-picker
                    id="dateProductionStart"
                    name="dateProductionStart"
                    placeholder="{{'order.columns.date.production.start.title'|translate}}"
                    title="{{'order.columns.date.production.start.description'|translate}}"
                    size="small"
                    [min]="element.production?.start.min"
                    [max]="element.production?.start.max"
                    [required]="true"
                    [disabled]="!wdEnvironmentService.permissions.canChangeProductionDate"
                    [dateFormat]="wdEnvironmentService.dateFormat"
                    [(model)]="element.production.start.date"
                    (modelChange)="updateOrderDate(element, 'production', 'start')"
                  >
                  </bc-atoms-date-picker>
                </td>
              </ng-container>

              <!--Production Date End-->
              <ng-container matColumnDef="dateProductionEnd">
                <th
                  class="date-column production"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.date.production.end.description'|translate}}"
                >
                  {{'order.columns.date.production.end.title'|translate}}
                </th>
                <td
                  class="date-column production bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.date.production.end.title'|translate"
                  [title]="element.production.end?.date"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-date-picker
                    id="dateProductionEnd"
                    name="dateProductionEnd"
                    placeholder="{{'order.columns.date.production.end.title'|translate}}"
                    title="{{'order.columns.date.production.end.description'|translate}}"
                    size="small"
                    [min]="element.production?.end.min"
                    [max]="element.production?.end.max"
                    [required]="true"
                    [disabled]="!wdEnvironmentService.permissions.canChangeProductionDate"
                    [dateFormat]="wdEnvironmentService.dateFormat"
                    [(model)]="element.production.end.date"
                    (modelChange)="updateOrderDate(element, 'production', 'end')"
                  >
                  </bc-atoms-date-picker>
                </td>
              </ng-container>

              <!--Weight-->
              <ng-container
                matColumnDef="weight"
                *ngIf="wdEnvironmentService.permissions.canViewWeight"
              >
                <th
                  class="weight-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.weight.description'|translate}}"
                >
                  {{'order.columns.weight.title'|translate}}
                </th>
                <td
                  class="weight-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.weight.title'|translate"
                  [title]="element.totals.weight.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                  (click)="openOrderAdjustmentsModal(element, 'weight')"
                >
                  <!--Editable-->
                  <a
                    href="javascript:void(0);"
                    *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="edit"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    {{element.totals.weight.label}}
                  </a>
                  <!--Not Editable-->
                  <span
                    *ngIf="!wdEnvironmentService.permissions.canManageAdjustments"
                  >
                    {{element.totals.weight.label}}
                  </span>
                </td>
              </ng-container>

              <!--Price-->
              <ng-container
                matColumnDef="price"
                *ngIf="wdEnvironmentService.permissions.canViewPricing"
              >
                <th
                  class="price-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.price.description'|translate}}"
                >
                  {{'order.columns.price.title'|translate}}
                </th>
                <td
                  class="price-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.price.title'|translate"
                  [title]="element.totals.price.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                  (click)="openOrderAdjustmentsModal(element, 'price')"
                >
                  <a
                    href="javascript:void(0);"
                    *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="edit"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    {{element.totals.price.label}}
                  </a>
                  <span
                    *ngIf="!wdEnvironmentService.permissions.canManageAdjustments"
                  >
                    {{element.totals.price.label}}
                  </span>
                </td>
              </ng-container>

              <!--Labour-->
              <ng-container
                matColumnDef="labour"
                *ngIf="wdEnvironmentService.permissions.canViewFabricationTime"
              >
                <th
                  class="labour-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.labour.description'|translate}}"
                >
                  {{'order.columns.labour.title'|translate}}
                </th>
                <td
                  class="labour-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.labour.title'|translate"
                  [title]="element.totals.shopLabour.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                  (click)="openOrderAdjustmentsModal(element, 'labour')"
                >
                  <a
                    href="javascript:void(0);"
                    *ngIf="wdEnvironmentService.permissions.canManageAdjustments"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="edit"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    {{element.totals.shopLabour.label}}
                  </a>
                  <span
                    *ngIf="!wdEnvironmentService.permissions.canManageAdjustments"
                  >
                    {{element.totals.shopLabour.label}}
                  </span>
                </td>
              </ng-container>

              <!--Items-->
              <ng-container matColumnDef="items">
                <th
                  class="items-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.items.description'|translate}}"
                >
                  {{'order.columns.items.title'|translate}}
                </th>
                <td
                  class="items-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.items.title'|translate"
                  [title]="element.meta.items.count"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span>{{element.meta.items.count}}</span>
                </td>
              </ng-container>

              <!--Item Progress-->
              <ng-container matColumnDef="itemsProgress">
                <th
                  class="progress-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.items.progress.description'|translate}}"
                >
                  {{'order.columns.items.progress.title'|translate}}
                </th>
                <td
                  class="progress-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.items.progress.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    class="progress"
                    [ngClass]="{ bordered: element.meta.items.progress.count === 0 }"
                    [title]="element.meta.items.progress.count + ' ' + ('statuses.complete'|translate|lowercase) + '\n' +
                        (element.meta.items.count-element.meta.items.progress.count) + ' ' + ('statuses.remaining'|translate|lowercase) + '\n' +
                        element.meta.items.count + ' ' + ('statuses.total'|translate|lowercase)"
                    *ngIf="element.meta.items.count > 0"
                  >
                    <div
                      class="progress-bar"
                      *ngIf="element.meta.items.progress.count > 0"
                      [ngStyle]="{ width: (element.meta.items.progress.ratio * 100) + '%' }"
                      [ngClass]="{ 'bg-success': (element.meta.items.count === element.meta.items.progress.count),
                        'bg-info': (element.meta.items.count > element.meta.items.progress.count) }"
                    >
                      {{element.meta.items.progress.count}}
                    </div>
                    <div
                      class="progress-bar bg-light"
                      [ngClass]="{ bordered: element.meta.items.progress.count !== 0 }"
                      *ngIf="element.meta.items.progress.count < element.meta.items.count"
                      [ngStyle]="{ width: (((element.meta.items.count - element.meta.items.progress.count) /
                        element.meta.items.count) * 100) + '%' }"
                    >
                      {{element.meta.items.count -
                      element.meta.items.progress.count}}
                    </div>
                  </span>
                </td>
              </ng-container>

              <!--Production Status-->
              <ng-container matColumnDef="productionStatus">
                <th
                  class="status-column production"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.status.production.description'|translate}}"
                >
                  {{'order.columns.status.production.title'|translate}}
                </th>
                <td
                  class="status-column production bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.status.production.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-dropdown
                    labelKey="label"
                    iconKey="type"
                    size="small"
                    display="block"
                    [disabled]="true"
                    [title]="element.production.status.label"
                    [requireSelection]="true"
                    [loading]="element.isUpdatingOrder"
                    [options]="wdOrdersService.orderFilters.value.productionStatuses"
                    [(model)]="element.production.status"
                  >
                  </bc-atoms-dropdown>
                </td>
              </ng-container>

              <!--*********************************** Fulfillment Columns ****************************************** -->

              <!--Fulfillment Status Status-->
              <ng-container
                [matColumnDef]="wdOrdersService.fulfillmentColumnPrefix + group.index + '-' + group.code"
                *ngFor="let group of wdOrdersService.orderOptions.value.orderFulfillmentGroups"
              >
                <th
                  class="status-column fulfillment {{group.code}}"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{group.label}}"
                >
                  {{group.code}}
                </th>
                <td
                  class="status-column fulfillment {{group.code}} bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="group.label"
                  [title]="group.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <!--Loop order fulfillment groups-->
                  <span *ngFor="let orderGroup of element.fulfillment?.groups">
                    <!--Check if group should be displayed-->
                    <div *ngIf="isMatchingFulfillmentGroup(group, orderGroup)">
                      <div
                        class="status-value fg-icon inactive"
                        *ngIf="!orderGroup.status?.type"
                      >
                        <bc-atoms-icon
                          type="cancel"
                          [title]="orderGroup.status?.label"
                        ></bc-atoms-icon>
                      </div>
                      <div
                        class="status-value fg-icon active"
                        *ngIf="orderGroup.status?.type"
                      >
                        <!--Icon Only-->
                        <div
                          *ngIf="!wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                        >
                          <bc-atoms-icon
                            [type]="orderGroup.status?.type"
                            [title]="orderGroup.status?.label"
                          ></bc-atoms-icon>
                        </div>
                        <!--Editable Dropdown-->
                        <div
                          *ngIf="wdEnvironmentService.permissions.canChangeFulfillmentGroupStatus"
                        >
                          <!--Fulfillment Groups-->
                          <div *ngIf="orderGroup.module !== 'tracker'">
                            <bc-atoms-dropdown
                              labelKey="label"
                              iconKey="type"
                              size="small"
                              [iconOnly]="true"
                              [title]="orderGroup.status?.label"
                              [requireSelection]="true"
                              [loading]="orderGroup.isUpdatingOrder"
                              [options]="wdOrdersService.orderFilters.value.productionStatuses"
                              [(model)]="orderGroup.status"
                              (modelChange)="updateFulfillmentGroupStatus(element, orderGroup)"
                            >
                            </bc-atoms-dropdown>
                          </div>
                          <!--Tracker Groups-->
                          <div *ngIf="orderGroup.module === 'tracker'">
                            <a
                              [href]="element.resources.toolTrackerOrderUrl"
                              target="_blank"
                            >
                              <!--@todo Once vertical slicing is removed we can replace the href with angular navigation-->
                              <bc-atoms-button
                                size="small"
                                [title]="orderGroup.status?.label"
                                [icon]="orderGroup.status.type"
                                [iconOnly]="true"
                                [loading]="orderGroup.isUpdatingOrder"
                                [disabled]="orderGroup.isUpdatingOrder"
                              >
                              </bc-atoms-button>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </span>
                  <!-- Display alternate text if no matching fulfillment group -->
                  <span
                    *ngIf="!hasMatchingFulfillmentGroup(group, element.fulfillment?.groups)"
                    class="status-value fg-icon inactive"
                  >
                    <bc-atoms-icon type="cancel"></bc-atoms-icon>
                  </span>
                </td>
              </ng-container>

              <!--Labour Progress-->
              <ng-container matColumnDef="labourProgress">
                <th
                  class="progress-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.labour.progress.description'|translate}}"
                >
                  {{'order.columns.labour.progress.title'|translate}}
                </th>
                <td
                  class="progress-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.labour.progress.title'|translate"
                  [title]="element.totals.shopLabourComplete.label + '/' + element.totals.shopLabour.label"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <!--Progress-->
                  <span
                    class="progress"
                    [ngClass]="{ bordered: element.totals.shopLabourComplete.value === 0 }"
                    *ngIf="element.totals.shopLabour.value"
                  >
                    <div
                      class="progress-bar bg-success"
                      *ngIf="element.totals.shopLabourComplete.value > 0"
                      [ngStyle]="{ width: ((element.totals.shopLabourComplete.value/element.totals.shopLabour.value) * 100) + '%' }"
                    >
                      <span
                        *ngIf="(element.totals.shopLabourComplete.value/element.totals.shopLabour.value) * 100 > 25"
                      >
                        {{element.totals.shopLabourComplete.label}}
                      </span>
                    </div>
                    <div
                      class="progress-bar bg-light"
                      [ngClass]="{ bordered: element.totals.shopLabourComplete.value !== 0 }"
                      *ngIf="element.totals.shopLabourRemain.value"
                      [ngStyle]="{ width: ((element.totals.shopLabourRemain.value/element.totals.shopLabour.value) * 100) + '%' }"
                    >
                      <span
                        *ngIf="(element.totals.shopLabourRemain.value/element.totals.shopLabour.value) * 100 > 25"
                      >
                        {{element.totals.shopLabourRemain.label}}
                      </span>
                    </div>
                  </span>
                </td>
              </ng-container>

              <!--************************************* Shipping Columns ******************************************* -->

              <!--Shipping Date-->
              <ng-container matColumnDef="dateShipping">
                <th
                  class="date-column shipping"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.date.shipping.description'|translate}}"
                >
                  {{'order.columns.date.shipping.title'|translate}}
                </th>
                <td
                  class="date-column shipping bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.date.shipping.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-date-picker
                    id="dateShipping"
                    name="dateShipping"
                    placeholder="{{'order.columns.date.shipping.title'|translate}}"
                    title="{{'order.columns.date.shipping.description'|translate}}"
                    size="small"
                    [min]="element.delivery?.start?.min"
                    [max]="element.delivery?.start?.max"
                    [required]="true"
                    [disabled]="!wdEnvironmentService.permissions.canChangeDeliveryDate"
                    [dateFormat]="wdEnvironmentService.dateFormat"
                    [(model)]="element.delivery.start.date"
                    (modelChange)="updateOrderDate(element, 'delivery', 'start')"
                  >
                  </bc-atoms-date-picker>
                </td>
              </ng-container>

              <!--Shipping Window Start-->
              <ng-container matColumnDef="shippingWindowStart">
                <th
                  class="time-column start col-xs-1"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.shipping.start.description'|translate}}"
                >
                  {{'order.columns.shipping.start.title'|translate}}
                </th>
                <td
                  class="time-column start col-xs-1 bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.shipping.start.title'|translate"
                  [title]="element.delivery.time.start | date : 'hh:mm aaa'"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    >{{element.delivery.time.start | date : "hh:mm aaa" }}</span
                  >
                </td>
              </ng-container>

              <!--Shipping Window End-->
              <ng-container matColumnDef="shippingWindowEnd">
                <th
                  class="time-column end"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  [attr.data-title]="'order.columns.shipping.end.title'|translate"
                >
                  {{'order.columns.shipping.end.title'|translate}}
                </th>
                <td
                  class="time-column end bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.shipping.end.title'|translate"
                  [title]="element.delivery.time.end | date : 'hh:mm aaa'"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    >{{element.delivery.time.end | date : "hh:mm aaa" }}</span
                  >
                </td>
              </ng-container>

              <!--Shipping Option-->
              <ng-container matColumnDef="shippingOption">
                <th
                  class="options-column shipping"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  [attr.data-title]="'order.columns.shipping.option.title'|translate"
                >
                  {{'order.columns.shipping.option.title'|translate}}
                </th>
                <td
                  class="options-column shipping bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.shipping.option.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-dropdown
                    title="{{element.delivery.option?.label}}"
                    display="block"
                    labelKey="label"
                    iconKey="type"
                    size="small"
                    [requireSelection]="true"
                    [loading]="element.isUpdatingOrder"
                    [disabled]="!wdEnvironmentService.permissions.canEditOrderDetails"
                    [options]="wdOrdersService.orderOptions.value.orderShippingTypes"
                    [(model)]="element.delivery.option"
                    (modelChange)="updateShippingOption(element)"
                  >
                  </bc-atoms-dropdown>
                </td>
              </ng-container>

              <!--Address-->
              <ng-container matColumnDef="shippingAddress">
                <th
                  class="address-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.shipping.address.description'|translate}}"
                >
                  {{'order.columns.shipping.address.title'|translate}}
                </th>
                <td
                  class="address-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.shipping.address.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <span
                    class="address-container"
                    [ngbPopover]="addressDetailsPopover"
                    [autoClose]="'outside'"
                    placement="bottom"
                    triggers="mouseenter:mouseleave:click"
                    #addressPopover="ngbPopover"
                    (click)="toggleAddressPopover(addressPopover, element.delivery.address)"
                    (mouseenter)="toggleAddressPopover(addressPopover, element.delivery.address)"
                    (mouseleave)="toggleAddressPopover(addressPopover, element.delivery.address)"
                  >
                    {{ element.delivery.address?.label }}
                  </span>
                </td>
              </ng-container>

              <!--Loading Zone-->
              <ng-container matColumnDef="loadingZoneOption">
                <th
                  class="options-column loading-zone"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.shipping.loadingZone.description'|translate}}"
                >
                  {{'order.columns.shipping.loadingZone.title'|translate}}
                </th>
                <td
                  class="options-column loading-zone bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.shipping.loadingZone.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-dropdown
                    [ngClass]="{'none-set': !element.delivery.loadingZone}"
                    display="block"
                    labelKey="label"
                    iconKey="type"
                    size="small"
                    title="{{element.delivery.loadingZone?.label}}"
                    placeholder="{{'order.shipping.loadingZone.title'|translate}}"
                    label="{{'order.shipping.loadingZone.default'|translate}}"
                    [requireSelection]="false"
                    [loading]="element.isUpdatingOrder"
                    [disabled]="!wdEnvironmentService.permissions.canEditOrderDetails"
                    [options]="wdOrdersService.orderOptions.value.orderShippingLoadingZones"
                    [(model)]="element.delivery.loadingZone"
                    (modelChange)="updateShippingLoadingZone(element)"
                  >
                  </bc-atoms-dropdown>
                </td>
              </ng-container>

              <!--Assigned Vehicle(s) for Shipping-->
              <ng-container matColumnDef="shippingAssignments">
                <th
                  class="assignments-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.shipping.assignments.description'|translate}}"
                >
                  {{'order.columns.shipping.assignments.title'|translate}}
                </th>
                <td
                  class="assignments-column bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.shipping.assignments.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                  [title]="getAssignmentsColumnTitle(element)"
                  (click)="element.delivery.vehicle.assignmentOptions?.length === 0 ? addVehicleAssignmentForOrder(element) : openVehicleAssignmentsModal(element)"
                >
                  <!--No Vehicle Assignment Options-->
                  <a
                    href=""
                    *ngIf="element.delivery.vehicle.assignmentOptions?.length === 0"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="add"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    <a href="">
                      {{'order.columns.shipping.assignments.add.title'|translate}}
                    </a>
                  </a>

                  <!--Vehicle Assignment Options - None Selected-->
                  <a
                    href=""
                    *ngIf="element.delivery.vehicle.assignmentOptions?.length > 0 && element.delivery.vehicle.assignments?.length === 0"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="edit"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    {{'order.columns.shipping.assignments.none.title'|translate}}
                  </a>

                  <!--Vehicle Assignment Options - One Selected-->
                  <a
                    href=""
                    *ngIf="element.delivery.vehicle.assignmentOptions?.length > 0 && element.delivery.vehicle.assignments?.length === 1"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="edit"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    <a href="">
                      {{ element.delivery.vehicle.assignments[0].labelShort }}
                    </a>
                  </a>

                  <!--Vehicle Assignment Options - Multiple Selected-->
                  <a
                    href=""
                    *ngIf="element.delivery.vehicle.assignmentOptions?.length > 0 && element.delivery.vehicle.assignments?.length > 1"
                  >
                    <div class="pull-left">
                      <bc-atoms-icon
                        type="edit"
                        *ngIf="!element.isUpdating"
                      ></bc-atoms-icon>
                      <bc-atoms-icon
                        type="loading"
                        [rotate]="true"
                        *ngIf="element.isUpdating"
                      ></bc-atoms-icon>
                    </div>
                    <a href="">
                      <strong
                        >{{element.delivery.vehicle.assignments.length}}</strong
                      >
                      {{'order.columns.shipping.assignments.title'|translate}}
                    </a>
                  </a>
                </td>
              </ng-container>

              <!--Shipping Status-->
              <ng-container matColumnDef="shippingStatus">
                <th
                  class="status-column shipping"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.status.shipping.description'|translate}}"
                >
                  {{'order.columns.status.shipping.title'|translate}}
                </th>
                <td
                  class="status-column shipping bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.status.shipping.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-dropdown
                    labelKey="label"
                    iconKey="type"
                    size="small"
                    display="block"
                    [disabled]="!wdEnvironmentService.permissions.canChangeDeliveryStatus"
                    [title]="element.delivery.status.label"
                    [requireSelection]="true"
                    [loading]="element.isUpdatingOrder"
                    [options]="wdOrdersService.orderFilters.value.deliveryStatuses"
                    [(model)]="element.delivery.status"
                    (modelChange)="updateShippingStatus(element)"
                  >
                  </bc-atoms-dropdown>
                </td>
              </ng-container>

              <!--*************************************** Core Columns ********************************************* -->

              <!--Messages-->
              <ng-container matColumnDef="orderMessages">
                <th
                  class="button-column messages static-column"
                  mat-header-cell
                  *matHeaderCellDef
                  title="{{'order.columns.messages.description'|translate}}"
                >
                  <bc-atoms-icon
                    type="email"
                    title="{{'order.columns.messages.description'|translate}}"
                  ></bc-atoms-icon>
                </th>
                <td
                  class="button-column messages bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.messages.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-button
                    [title]="('order.columns.messages.title'|translate) + ': ' + element.messages"
                    icon="email"
                    display="block"
                    size="small"
                    [disabled]="false"
                    [iconOnly]="true"
                    *ngIf="element.messages > 0"
                  >
                  </bc-atoms-button>
                  <span *ngIf="element.messages" class="floating-badge"
                    >{{element.messages}}</span
                  >
                </td>
              </ng-container>

              <!--Attachments-->
              <ng-container matColumnDef="orderAttachments">
                <th
                  class="button-column attachments static-column"
                  mat-header-cell
                  *matHeaderCellDef
                  title="{{'order.columns.attachments.description'|translate}}"
                >
                  <bc-atoms-icon
                    type="attachment"
                    title="{{'order.columns.attachments.description'|translate}}"
                  ></bc-atoms-icon>
                </th>
                <td
                  class="button-column attachments bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.attachments.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-button
                    title="{{'exports.attachments.title'|translate }}"
                    icon="attachment"
                    display="block"
                    size="small"
                    [disabled]="false"
                    [iconOnly]="true"
                    *ngIf="element.attachments.length > 0"
                  >
                    <div dropdown-content>
                      <form class="m-2">
                        <!--Reports-->
                        <bc-atoms-input-group
                          labelIcon="all"
                          labelTitle="{{'exports.attachments.title'|translate}}"
                          labelDescription="{{'exports.attachments.description'|translate}}"
                        >
                          <div
                            *ngFor="let attachment of element.attachments; let i = index"
                          >
                            <a [href]="attachment.url" *ngIf="attachment.url">
                              <div class="dropdown-item">
                                <label
                                  >{{i+1}}.
                                  {{attachment.originalFileName}}</label
                                >
                              </div>
                            </a>
                          </div>
                        </bc-atoms-input-group>
                      </form>
                    </div>
                  </bc-atoms-button>
                </td>
              </ng-container>

              <!--Exports-->
              <ng-container matColumnDef="orderExports">
                <th
                  class="button-column exports static-column"
                  mat-header-cell
                  *matHeaderCellDef
                  title="{{'order.columns.exports.description'|translate}}"
                >
                  <bc-atoms-icon
                    type="export"
                    title="{{'order.columns.exports.description'|translate}}"
                  ></bc-atoms-icon>
                </th>
                <td
                  class="button-column exports bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.exports.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-button
                    title="{{'exports.reports.title'|translate }}"
                    icon="export"
                    display="block"
                    size="small"
                    [disabled]="false"
                    [iconOnly]="true"
                  >
                    <div dropdown-content>
                      <form class="m-2">
                        <!--Reports-->
                        <bc-atoms-input-group
                          *ngIf="element?.reports?.length > 0"
                          labelIcon="all"
                          labelTitle="{{'exports.reports.title'|translate}}"
                          labelDescription="{{'exports.reports.description'|translate}}"
                        >
                          <div *ngFor="let reportElem of element.reports">
                            <a
                              [href]="reportElem.url"
                              [target]="reportElem.target"
                              *ngIf="reportElem.url"
                            >
                              <div class="dropdown-item">
                                <bc-atoms-icon
                                  [type]="reportElem.icon"
                                ></bc-atoms-icon>
                                <label>{{reportElem.label}}</label>
                              </div>
                            </a>
                          </div>
                        </bc-atoms-input-group>

                        <!--Exports-->
                        <bc-atoms-input-group
                          *ngIf="element?.exports?.length > 0"
                          labelIcon="all"
                          labelTitle="{{'exports.title'|translate}}"
                          labelDescription="{{'exports.description'|translate}}"
                        >
                          <div *ngFor="let exportElem of element.exports">
                            <a
                              [href]="exportElem.url"
                              [target]="exportElem.target"
                              *ngIf="exportElem.url"
                            >
                              <div class="dropdown-item">
                                <bc-atoms-icon
                                  [type]="exportElem.icon"
                                ></bc-atoms-icon>
                                <label>{{exportElem.label}}</label>
                              </div>
                            </a>
                          </div>
                        </bc-atoms-input-group>

                        <!--Downloads (CAM export, etc.)-->
                        <bc-atoms-input-group
                          *ngIf="element?.downloads?.length > 0"
                          labelIcon="all"
                          labelTitle="{{'exports.downloads.title'|translate}}"
                          labelDescription="{{'exports.downloads.description'|translate}}"
                        >
                          <div *ngFor="let downloadElem of element.downloads">
                            <a
                              [href]="downloadElem.url"
                              [target]="downloadElem.target"
                              *ngIf="downloadElem.url"
                            >
                              <div class="dropdown-item">
                                <bc-atoms-icon
                                  [type]="downloadElem.icon"
                                ></bc-atoms-icon>
                                <label>{{downloadElem.label}}</label>
                              </div>
                            </a>
                          </div>
                        </bc-atoms-input-group>
                      </form>
                    </div>
                  </bc-atoms-button>
                </td>
              </ng-container>

              <!--Order Status-->
              <ng-container matColumnDef="orderStatus">
                <th
                  class="status-column order static-column"
                  mat-header-cell
                  mat-sort-header
                  *matHeaderCellDef
                  title="{{'order.columns.status.description'|translate}}"
                >
                  <bc-atoms-icon
                    type="error"
                    title="{{'order.columns.status.description'|translate}}"
                  ></bc-atoms-icon>
                </th>
                <td
                  class="status-column order bcx-{{element.status?.type}}-row bcx-{{element.production.status?.type}}-row"
                  mat-cell
                  *matCellDef="let element"
                  [attr.data-title]="'order.columns.status.title'|translate"
                  [ngClass]="{ disabled: wdOrdersService.isReloadingOrders.value === true }"
                >
                  <bc-atoms-dropdown
                    labelKey="label"
                    iconKey="type"
                    size="small"
                    display="block"
                    [iconOnly]="true"
                    [title]="element.status?.label"
                    [requireSelection]="true"
                    [disabled]="!wdEnvironmentService.permissions.canChangeOrderStatus"
                    [loading]="element?.isUpdatingOrder"
                    [options]="wdOrdersService.orderFilters.value.orderStatuses"
                    [(model)]="element.status"
                    (modelChange)="updateOrderStatus(element)"
                  >
                  </bc-atoms-dropdown>
                </td>
              </ng-container>
            </bc-templates-table-container>
          </div>
          <!--Order Tabs-->
          <div class="col-sm-12 text-center tabs-bottom">
            <app-orders-list-tabs-component
              [orderType]="orderType"
            ></app-orders-list-tabs-component>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!--Footer-->
  <div class="order-footer row mt-3">
    <div class="w-100" *ngIf="!wdOrdersService.isLoadingOrders.value">
      <app-orders-list-v2-header-component
        [orderType]="orderType"
        [pageTitle]="pageTitle"
        [pageSubtitle]="pageSubtitle"
        [showHeader]="false"
        (tabChanges)="changeDetector.detectChanges()"
      ></app-orders-list-v2-header-component>
    </div>
  </div>
</form>

<!--Address Popover-->
<ng-template #addressDetailsPopover let-address="address">
  <app-popover-address [address]="address"></app-popover-address>
</ng-template>

<!--Adjustments Popover-->
<ng-template #adjustmentsPopover let-count="count">
  <app-popover-adjustment [count]="count"></app-popover-adjustment>
</ng-template>

<!--Override Popover-->
<ng-template
  #popContentOverride
  let-order="order"
  let-popover="popover"
  let-type="type"
  let-label="label"
>
  <app-popover-override
    [order]="order"
    [popover]="popover"
    [type]="type"
    [label]="label"
  ></app-popover-override>
</ng-template>

<!--User Popover-->
<ng-template #userDetailsPopover let-user="user">
  <app-popover-user [user]="user"></app-popover-user>
</ng-template>
